import React, { useEffect } from 'react';
import { View } from '../../../../../components/Common/react-native-components';
import MultiFeatureCard from '../../../../../components/Common/Card/MultiOptionsCard/MultiFeatureCard';
import TabbedHighchartsContainer from '../../../../../components/Common/HighCharts/TabbedHighchartsContainer';
import useTransformChartData from '../../../../../components/Common/HighCharts/TabbedHighchartsContainer/useTransformChartData';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import useExpandableGridHelper from '../../../hooks/useExpandableGridHelper';
import { ActivityIndicator } from '@hmsexp/component-library';

const AggregateData = props => {
  const { itemData, timeperiodLabel, widgetId, applied_filter_values, reduxData } = props;
  const dispatch = useDispatch();
  const chartData = get(reduxData, 'chartData.default.default.data', {});

  const { fetchData, loadingData } = useExpandableGridHelper({
    widgetId,
    appliedFilterValues: applied_filter_values,
    onAggregationResponseFn: ({ rawData, widgetId }) => {
      dispatch({
        type: 'SAVE_WIDGET_CHART_DATA',
        payload: {
          widgetId,
          metricType: 'default',
          chartData: rawData,
          currentLevel: 'default'
        }
      });
    }
  });

  const mainChartConfig = {
    chart: {
      type: 'line',
      height: 300,
      backgroundColor: '#fff'
    },
    title: {
      text: ' ',
      align: 'left',
      style: { fontSize: '16px', fontWeight: 'bold' }
    },
    xAxis: {
      categories: [],
      crosshair: true
    },
    yAxis: {
      title: { text: null }
    },
    tooltip: {
      shared: true,
      useHTML: true,
      followPointer: true,
      outside: true,
      backgroundColor: 'none',
      borderWidth: 0,
      shadow: false,
      formatter: function () {
        let body = '';
        this.points.forEach(point => {
          body += `
                    <div style="display: flex; align-items: center; justify-content: space-between;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <div style="
                          width: 10px; height: 10px;
                          background: ${point.color};
                        "></div>
                        <div>${point.series.name} &nbsp;&nbsp;&nbsp;&nbsp;</div>
                      </div>
                      <span style="font-weight: 600;">${point.y}</span>
                    </div>`;
        });

        return `
                   <div style="
                     background: #1e222c;
                     color: #fff;
                     padding: 12px 18px;
                     border-radius: 14px;
                     font-size: 14px;
                     box-shadow: 0 4px 14px rgba(0,0,0,0.25);
                     display: flex;
                     flex-direction: column;
                     gap: 6px;
                     pointer-events: none;
                   ">
                     <div style="font-size: 14px; font-weight: 600;">${this.x}</div>
                     ${body}
                   </div>
             
                   <div style="
                     width: 0; height: 0; margin: 0 auto;
                     border-left: 10px solid transparent;
                     border-right: 10px solid transparent;
                     border-top: 12px solid #1e222c;
                   "></div>
                 `;
      },
      positioner: function (labelWidth, labelHeight, point) {
        return {
          x: point.plotX + this.chart.plotLeft - labelWidth / 2,
          y: point.plotY + this.chart.plotTop - labelHeight
        };
      }
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      series: {
        lineWidth: 2,
        shadow: {
          color: 'rgba(0, 0, 0, 0.25)',
          offsetX: 0,
          offsetY: 5,
          opacity: 1,
          width: 4
        },
        marker: { enabled: true },
        states: {
          inactive: {
            opacity: 1
          }
        }
      }
    },
    credits: { enabled: false },
    exporting: { enabled: false }
  };
  const sparklineConfig = {
    chart: {
      type: 'line',
      height: 50,
      margin: [5, 5, 5, 0],
      backgroundColor: 'transparent'
    },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      visible: false,
      labels: { enabled: false },
      title: { text: null },
      startOnTick: false,
      endOnTick: false,
      tickPositions: []
    },
    yAxis: {
      visible: false,
      labels: { enabled: false },
      title: { text: null },
      startOnTick: false,
      endOnTick: false,
      tickPositions: [0]
    },
    tooltip: { enabled: false },
    plotOptions: {
      series: {
        animation: false,
        lineWidth: 2,
        shadow: {
          color: 'rgba(0, 0, 0, 0.23)',
          offsetX: 0,
          offsetY: 5,
          opacity: 10,
          width: 4
        },
        states: { hover: { lineWidth: 2 } },
        marker: { radius: 1, states: { hover: { radius: 2 } } },
        fillOpacity: 0.25
      },
      column: { negativeColor: '#910000', borderColor: 'silver' }
    }
  };

  useEffect(() => {
    if (widgetId) fetchData();
  }, [widgetId, fetchData]);

  const colors = ['#5c6bc0', '#d32f2f', 'green', '#ffa726', '#29b6f6'];
  const dynamicRowConfig = {};
  if (chartData && chartData.rows) {
    chartData.rows.forEach((row, index) => {
      dynamicRowConfig[row] = {
        label: get(itemData, `labelsMapping.${row}`, row),
        color: colors[index % colors.length],
        formatValue: val => {
          if (typeof val !== 'number' || Number.isNaN(val)) return val;
          return Number.isInteger(val) ? val : Number(val).toFixed(2);
        },
        formatSubValue: val => {
          if (typeof val !== 'number' || Number.isNaN(val)) return val;
          const formatted = Number.isInteger(val) ? String(val) : Number(val).toFixed(2);
          return val > 0 ? `+${formatted}` : formatted;
        }
      };
    });
  }
  const tabs = useTransformChartData(chartData, {
    summaryColumnMap: { value: 'total', subValue: 'delta' },
    excludeColumns: ['benchmark_total'],
    rowConfig: dynamicRowConfig,
    defaultSubLabel: 'Same Period Year Ago'
  });

  return (
    <View>
      <MultiFeatureCard
        title={get(itemData, 'title', 'Aggregate Data')}
        subTitle={get(itemData, 'subTitle', 'Time Period') + ' ' + timeperiodLabel}
        loading={loadingData}
        isNoData={!loadingData && !chartData?.rows?.length}
        style={{ childContainer: { minHeight: 250 } }}
        renderContent={() => (
          <>
            {loadingData && (
              <View style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ActivityIndicator height={40} width={40} />
              </View>
            )}

            {!loadingData && chartData && (
              <TabbedHighchartsContainer
                tabs={tabs}
                mainChartConfig={mainChartConfig}
                sparklineConfig={sparklineConfig}
                mode='single'
                disableTabClick={false}
                containerStyle={{ padding: 10 }}
              />
            )}
          </>
        )}
      />
    </View>
  );
};

export default AggregateData;
